import type { ReactNode } from 'react';
import { CheckIcon, CopyIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';

export interface CopyButtonProps {
  /** What to copy, or how to get it at click time (a rendered block's text, the current URL). */
  text: string | (() => string);
  /** The control's name at rest — "Copy", "Copy code", "Copy link to this task". */
  label: string;
  copiedLabel?: string;
  failedLabel?: string;
  /** The icon at rest. Defaults to the clipboard; the drawer's link copy shows a link. */
  idleIcon?: ReactNode;
  className?: string;
  /**
   * Also say the outcome in a polite live region (spec 236 FR-006). The button's
   * renamed label alone is read out only by some screen readers, and only while
   * it has focus; a page whose copy is the point of the visit says it outright.
   */
  announce?: boolean;
}

/**
 * The one copy control (spec 096 FR-004, FR-008).
 *
 * Three surfaces copy something — a fenced code block, a tool block, the task's
 * own link — and each used to carry its own state, timer and icon switch. One
 * control means one answer to the question the clipboard never answers itself:
 * did it work? "Copied" for a moment when it did; the failure named when it did
 * not, because a refused clipboard (an insecure origin, a denied permission) is
 * silent, and a button that snaps back to "Copy" has told the reader their text
 * is on the clipboard when it is not.
 */
export function CopyButton({
  text,
  label,
  copiedLabel = 'Copied',
  failedLabel = 'Copy failed',
  idleIcon,
  className,
  announce = false,
}: CopyButtonProps) {
  const { copy, state } = useCopyToClipboard();
  const current =
    state === 'copied' ? copiedLabel : state === 'failed' ? failedLabel : label;
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={current}
      title={current}
      className={className}
      onClick={() => {
        void copy(typeof text === 'function' ? text() : text);
      }}
    >
      {state === 'copied' ? (
        <CheckIcon aria-hidden="true" strokeWidth={1.5} />
      ) : state === 'failed' ? (
        <XIcon aria-hidden="true" strokeWidth={1.5} />
      ) : (
        (idleIcon ?? <CopyIcon aria-hidden="true" strokeWidth={1.5} />)
      )}
    </Button>
  );
  if (!announce) return button;
  return (
    <>
      {button}
      {/* Always present, so the region exists before it has anything to say. */}
      <span role="status" className="sr-only">
        {state === 'idle' ? '' : current}
      </span>
    </>
  );
}
